import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ChevronDown, ChevronRight, History, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { errorMessage, useBbWebhook, useBbWebhooks } from "../../api";
import { EmptyBlock, ErrorBlock, FetchingDot, KeyValueGrid, LoadingBlock, SectionCard } from "../../components/primitives";
import { formatMs, formatStamp } from "../../format";
import type { BbKind, BbWebhook, BbWebhookFilters, BranchMeta, MonitoringWindow } from "../../types";
import { CopyButton } from "../events/bits";
import { KindChip, OutcomeChip, StepsLine } from "./bits";
import { ALL_OUTCOMES, BB_KINDS, OUTCOMES_BY_KIND, PROBLEM_OUTCOMES, outcomeLabel } from "./helpers";

const WINDOW_MS: Record<MonitoringWindow, number> = {
  "1h": 3_600_000,
  "6h": 6 * 3_600_000,
  "24h": 86_400_000,
  "7d": 7 * 86_400_000,
};
const PAGE = 100;
const MAX_LIMIT = 1000;
const PROBLEMS = "__problems";
const ALL = "__all";

/** The logged webhooks of the board window, filterable, newest first; a row opens payload + decision. */
export function BbWebhookLogCard({
  window,
  branches,
  onOpen,
}: {
  window: MonitoringWindow;
  branches: BranchMeta[];
  onOpen: (itemId: string) => void;
}) {
  const { t } = useLocalization();
  const [kind, setKind] = useState<BbKind | null>(null);
  const [outcome, setOutcome] = useState<string>(ALL);
  const [branchId, setBranchId] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState<string | null>(null);

  // the search runs when typing pauses, not on every key
  useEffect(() => {
    const timer = setTimeout(() => setQ(text), 300);
    return () => clearTimeout(timer);
  }, [text]);
  useEffect(() => setLimit(PAGE), [kind, outcome, branchId, q, window]);

  // a stable start of the window (minute precision), so polling does not change the query key
  const minute = Math.floor(Date.now() / 60_000);
  const sinceMs = useMemo(() => minute * 60_000 - WINDOW_MS[window], [minute, window]);

  const filters: BbWebhookFilters = {
    kinds: kind ? [kind] : [],
    outcomes: outcome === ALL ? [] : outcome === PROBLEMS ? PROBLEM_OUTCOMES : [outcome],
    branchId,
    q,
    itemId: null,
    limit,
  };
  const query = useBbWebhooks(filters, sinceMs);
  const rows = query.data?.rows ?? [];
  const outcomes = kind ? OUTCOMES_BY_KIND[kind] : ALL_OUTCOMES;
  const acquiring = branches.filter((b) => b.enable_acquisition);

  return (
    <SectionCard
      title={t("monitoring.bb.log.title")}
      description={t("monitoring.bb.log.description")}
      actions={<FetchingDot active={query.isFetching && !query.isPending} />}
      contentClassName="p-0"
    >
      <div className="flex flex-wrap items-center gap-2 border-b px-4 py-2.5">
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={kind ?? ALL}
          onValueChange={(value) => {
            if (!value) return;
            setKind(value === ALL ? null : (value as BbKind));
            setOutcome(ALL);
          }}
          aria-label={t("monitoring.bb.log.kind")}
          className="gap-0.5"
        >
          <ToggleGroupItem value={ALL} className="h-8 px-2.5 text-xs">
            {t("monitoring.bb.log.kind.all")}
          </ToggleGroupItem>
          {BB_KINDS.map((k) => (
            <ToggleGroupItem key={k} value={k} className="h-8 px-2.5 text-xs">
              {t(`monitoring.bb.kind.${k}`)}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <Select value={outcome} onValueChange={setOutcome}>
          <SelectTrigger className="h-8 w-[11.5rem] text-xs" aria-label={t("monitoring.bb.log.outcome")}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL} className="text-xs">{t("monitoring.bb.log.outcome.all")}</SelectItem>
            <SelectItem value={PROBLEMS} className="text-xs">{t("monitoring.bb.log.outcome.problems")}</SelectItem>
            <SelectSeparator />
            {outcomes.map((o) => (
              <SelectItem key={o} value={o} className="text-xs">
                {outcomeLabel(o)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {acquiring.length > 0 && (
          <Select value={branchId ?? ALL} onValueChange={(value) => setBranchId(value === ALL ? null : value)}>
            <SelectTrigger className="h-8 w-[10.5rem] text-xs" aria-label={t("monitoring.bb.log.branch")}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL} className="text-xs">{t("monitoring.bb.log.branch.all")}</SelectItem>
              {acquiring.map((b) => (
                <SelectItem key={b.id} value={b.id} className="text-xs">
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t("monitoring.bb.log.search")}
            aria-label={t("monitoring.bb.log.search")}
            className="h-8 pl-7 text-xs"
          />
        </div>
      </div>
      {query.isError && (
        <div className="px-4 pt-3">
          <ErrorBlock message={errorMessage(query.error)} onRetry={() => void query.refetch()} />
        </div>
      )}
      {!query.data ? (
        !query.isError && (
          <div className="p-4">
            <LoadingBlock height={200} />
          </div>
        )
      ) : rows.length === 0 ? (
        <div className="p-4">
          <EmptyBlock>{t("monitoring.bb.log.empty")}</EmptyBlock>
        </div>
      ) : (
        <>
          <ul className="max-h-[40rem] divide-y divide-border/60 overflow-y-auto">
            {rows.map((row) => (
              <WebhookRow
                key={row.id}
                row={row}
                open={open === row.id}
                onToggle={() => setOpen(open === row.id ? null : row.id)}
                onOpenPizza={onOpen}
              />
            ))}
          </ul>
          {query.data.limited && (
            <div className="flex flex-wrap items-center justify-between gap-2 border-t px-4 py-2 text-[11px] text-muted-foreground">
              <span>{t("monitoring.bb.log.limited", { limit: query.data.limit })}</span>
              {limit < MAX_LIMIT && (
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={() => setLimit(Math.min(MAX_LIMIT, limit + 2 * PAGE))}>
                  {t("monitoring.bb.log.loadMore")}
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </SectionCard>
  );
}

/** What the row is about: the pizza line, or the order. */
function Subject({ row }: { row: BbWebhook }) {
  const { t } = useLocalization();
  if (row.kind === "item") {
    return (
      <>
        <span className="break-words font-medium">{row.name || row.item_id}</span>
        <span className="break-all font-mono text-[10px] text-muted-foreground">{row.item_id}</span>
      </>
    );
  }
  return (
    <>
      <span className="break-words">
        {t("monitoring.bb.log.order")}
        {row.kind === "order" && row.item_ids ? ` · ${t("monitoring.bb.log.items", { count: row.item_ids.length })}` : ""}
      </span>
      <span className="break-all font-mono text-[10px] text-muted-foreground">{row.order_id}</span>
    </>
  );
}

function WebhookRow({
  row,
  open,
  onToggle,
  onOpenPizza,
}: {
  row: BbWebhook;
  open: boolean;
  onToggle: () => void;
  onOpenPizza: (itemId: string) => void;
}) {
  const { t } = useLocalization();
  const Chevron = open ? ChevronDown : ChevronRight;
  return (
    <li className={cn(open && "bg-muted/20")}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full min-w-0 items-start gap-2 px-4 py-2 text-left hover:bg-muted/30 focus-visible:bg-muted/30 focus-visible:outline-none"
      >
        <Chevron className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs">
            <span className="w-[7.5rem] shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">{formatStamp(row.ts)}</span>
            <KindChip kind={row.kind} />
            <OutcomeChip outcome={row.outcome} />
            <span className="flex min-w-[11rem] flex-1 flex-col">
              <Subject row={row} />
            </span>
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 pl-0 text-[11px] text-muted-foreground sm:pl-[7.5rem]">
            {row.branch_name && <span className="break-words">{row.branch_name}</span>}
            {row.kind === "item" && <StepsLine steps={row.steps} />}
            {row.kind === "status" && (
              <span className="font-mono">
                {row.previous_status ? t("monitoring.bb.log.statusChange", { from: row.previous_status, to: row.status ?? "?" }) : row.status}
              </span>
            )}
            {row.kind === "order" && row.status && <span className="font-mono">{row.status}</span>}
            {row.camera && <span>{row.camera}</span>}
            {row.detail && <span className="break-all font-mono text-red-300/90">{row.detail}</span>}
          </div>
        </div>
      </button>
      {open && <WebhookDetail row={row} onOpenPizza={onOpenPizza} />}
    </li>
  );
}

function WebhookDetail({ row, onOpenPizza }: { row: BbWebhook; onOpenPizza: (itemId: string) => void }) {
  const { t } = useLocalization();
  const detail = useBbWebhook(row.id);
  const full = detail.data ?? row;
  const payload = detail.data?.payload;
  const text = payload === undefined || payload === null ? null : JSON.stringify(payload, null, 2);
  const items: { label: ReactNode; value: ReactNode }[] = [
    { label: t("monitoring.bb.field.received"), value: formatStamp(full.ts) },
  ];
  if (full.sent_at) items.push({ label: t("monitoring.bb.field.sentAt"), value: full.sent_at });
  if (full.event_type) items.push({ label: t("monitoring.bb.field.eventType"), value: full.event_type });
  if (full.duration_ms !== undefined) items.push({ label: t("monitoring.bb.field.processing"), value: formatMs(full.duration_ms) });
  if (full.item_id) items.push({ label: t("monitoring.bb.field.item"), value: full.item_id });
  if (full.order_id) items.push({ label: t("monitoring.bb.field.order"), value: full.order_id });
  if (full.branch_name || full.branch_id) items.push({ label: t("monitoring.bb.field.branch"), value: full.branch_name || full.branch_id });
  if (full.steps) items.push({ label: t("monitoring.bb.field.steps"), value: <StepsLine steps={full.steps} /> });
  if (full.status) items.push({ label: t("monitoring.bb.field.status"), value: full.status });
  if (full.pizza_known !== undefined)
    items.push({
      label: t("monitoring.bb.field.pizzaKnown"),
      value: t(full.pizza_known ? "monitoring.bb.field.pizzaKnown.yes" : "monitoring.bb.field.pizzaKnown.no"),
    });
  if (full.camera) items.push({ label: t("monitoring.bb.field.camera"), value: full.camera });
  if (full.trigger_ms !== undefined) items.push({ label: t("monitoring.bb.field.trigger"), value: formatMs(full.trigger_ms) });
  if (full.capture_count !== undefined) items.push({ label: t("monitoring.bb.field.captureCount"), value: full.capture_count });
  if (full.pizzas !== undefined) items.push({ label: t("monitoring.bb.field.pizzas"), value: full.pizzas });
  if (full.split !== undefined) items.push({ label: t("monitoring.bb.field.split"), value: full.split });
  if (full.detail) items.push({ label: t("monitoring.bb.field.detail"), value: full.detail });
  if (full.instance) items.push({ label: t("monitoring.bb.field.instance"), value: full.instance });
  const pizzaIds = row.kind === "item" && row.item_id ? [row.item_id] : row.kind === "order" ? row.item_ids ?? [] : [];

  return (
    <div className="flex min-w-0 flex-col gap-3 px-4 pb-3 pl-9">
      <div>
        <div className="mb-1 text-[11px] font-medium text-muted-foreground">{t("monitoring.bb.log.fields")}</div>
        <KeyValueGrid items={items} />
      </div>
      {pizzaIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {pizzaIds.map((id) => (
            <Button key={id} variant="outline" size="sm" className="h-7 gap-1.5 px-2 text-xs" onClick={() => onOpenPizza(id)}>
              <History className="h-3.5 w-3.5" aria-hidden />
              {t("monitoring.bb.log.timeline")}
              {pizzaIds.length > 1 && <span className="font-mono text-[10px] text-muted-foreground">{id.slice(0, 8)}</span>}
            </Button>
          ))}
        </div>
      )}
      <div className="min-w-0">
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium text-muted-foreground">{t("monitoring.bb.log.payload")}</span>
          {text && <CopyButton text={text} what={t("monitoring.bb.log.payload")} />}
        </div>
        {detail.isPending ? (
          <LoadingBlock height={80} />
        ) : detail.isError ? (
          <ErrorBlock message={errorMessage(detail.error)} onRetry={() => void detail.refetch()} />
        ) : text ? (
          <>
            <pre
              tabIndex={0}
              aria-label={t("monitoring.bb.log.payload")}
              className="max-h-96 min-w-0 overflow-auto whitespace-pre-wrap break-all rounded-md border bg-muted/30 p-2 font-mono text-[11px] leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              {text}
            </pre>
            {detail.data?.payload_truncated && (
              <p className="mt-1 text-[11px] text-amber-300">{t("monitoring.bb.log.payloadTruncated")}</p>
            )}
          </>
        ) : (
          <p className="text-[11px] text-muted-foreground">{t("monitoring.bb.log.payloadNone")}</p>
        )}
      </div>
    </div>
  );
}
